import { useCallback, useState } from "react";
import { useDisclosure, useToast } from "@chakra-ui/react";
import { useStores } from "stores/setup/use-store";
import { IStation } from "stores/data/oceanWeatherIntelligences/oceanWeatherIntelligenceModel";

interface UseSaveCustomPointProps {
    station: IStation | null;
    t: (key: string) => string;
    onSaveSuccess?: () => void;
}

interface UseSaveCustomPointReturn {
    isSaveModalOpen: boolean;
    onSaveModalOpen: () => void;
    onSaveModalClose: () => void;
    stationName: string;
    setStationName: (name: string) => void;
    isSaving: boolean;
    isSaved: boolean;
    handleSaveStation: () => Promise<void>;
    handleOpenSaveModal: () => void;
}

/**
 * Shared hook for saving a custom point as a station.
 * Used by MapPointPreview and PointSelectionMapControls.
 */
export function useSaveCustomPoint({
    station,
    t,
    onSaveSuccess,
}: UseSaveCustomPointProps): UseSaveCustomPointReturn {
    const toast = useToast();

    const {
        dataStores: { forecastChartPageStore, insightAlertStore },
    } = useStores();

    const {
        isOpen: isSaveModalOpen,
        onOpen: onSaveModalOpen,
        onClose: onSaveModalClose,
    } = useDisclosure();

    const [stationName, setStationName] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [isSaved, setIsSaved] = useState(false);

    const handleSaveStation = useCallback(async () => {
        if (!station || !stationName.trim()) return;

        setIsSaving(true);
        try {
            const macroRegionOrigin = station.macro_region_origin;
            if (!macroRegionOrigin) {
                toast({
                    title: t("noMacroRegionOriginWasFound"),
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });
                setIsSaving(false);
                return;
            }

            // Save the station using the forecastChartPageStore API
            await forecastChartPageStore.saveStationByUserAPI(
                stationName.trim(),
                station.station_lat,
                station.station_lon,
                macroRegionOrigin
            );

            // Check if save was successful by looking at the store state
            if (forecastChartPageStore.selectedStation > 0) {
                // Find the newly saved station from the stations list
                const savedStation = forecastChartPageStore.stations.find(
                    (s) => s.station_id === forecastChartPageStore.selectedStation
                );

                if (savedStation) {
                    // Update the selected station with the saved one (now has a real station_id)
                    insightAlertStore.setSelectedStationForAlert(savedStation);
                    insightAlertStore.setIsPointInvalid(false);
                }

                setIsSaved(true);
                toast({
                    title: t("stationSavedSuccess"),
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
                onSaveModalClose();
                setStationName("");
                onSaveSuccess?.();
            }
        } catch (error) {
            toast({
                title: t("errorSavingStation"),
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSaving(false);
        }
    }, [
        station,
        stationName,
        forecastChartPageStore,
        insightAlertStore,
        toast,
        t,
        onSaveModalClose,
        onSaveSuccess,
    ]);

    const handleOpenSaveModal = useCallback(() => {
        setStationName("");
        onSaveModalOpen();
    }, [onSaveModalOpen]);

    return {
        isSaveModalOpen,
        onSaveModalOpen,
        onSaveModalClose,
        stationName,
        setStationName,
        isSaving,
        isSaved,
        handleSaveStation,
        handleOpenSaveModal,
    };
}
